// What this clinic offers at the site a waitlist request was filed at.
//
// The portal loads `dashState.services` and `dashState.doctors` ONCE, for
// `patient.lid?.[0]` — the patient's home site. A waitlist entry carries its
// own `locationId`, which is not always that one. Editing an entry filed
// elsewhere was therefore handed the home site's catalogue, and a patient who
// changed their provider picked somebody who does not work where they are
// waiting. That is how `waitlist_entry_glVPw9PjPp36p3l1` came to sit at Coastal
// Health naming a clinician whose record reads `lid: ["central"]`.
//
// The server refuses that now. This stops it being offered in the first place,
// which is the half that can explain itself: a picker that never shows the
// option beats a save that fails.
//
// The clinic form is the reference — `forLocation` and `cliniciansForService`
// in `blhClinicApp/src/features/waitlist/components/waitlistEntryForm.js`. The
// two apps share no package, so this is a twin rather than an import.

/**
 * Every location a directory record is offered at.
 *
 * Three keys, because the data carries three. A scalar is ONE location, not
 * none: reading a bare string as an empty list would flip the record into the
 * "available everywhere" branch below, the exact opposite of what it says.
 */
function locationIdsOf(record) {
  const ids = (value) => {
    const list = Array.isArray(value) ? value : value == null ? [] : [value];
    return list
      .filter((id) => typeof id === "string" && id.trim().length > 0)
      .map((id) => id.trim());
  };
  return [
    ...ids(record?.lid),
    ...ids(record?.locationIds),
    ...ids(record?.locations),
  ];
}

/**
 * Services or clinicians available at one site.
 *
 * `lid` is an ARRAY on directory records, so this is MEMBERSHIP and never
 * equality.
 *
 * A record with NO location is kept, matching the same rule the server applies
 * at the write door: an absent field is not a claim about anywhere, so it never
 * contradicts the request. Hiding a record the server would accept leaves the
 * patient unable to pick it and unable to be told why.
 *
 * With no real location the answer is an empty list, not everything — an entry
 * carrying a service from another site is one nobody can fill.
 */
export function forLocation(records, locationId) {
  const list = Array.isArray(records) ? records : [];
  const site = typeof locationId === "string" ? locationId.trim() : "";
  if (!site) return [];
  return list.filter((record) => {
    const locations = locationIdsOf(record);
    return locations.length === 0 || locations.includes(site);
  });
}

/**
 * Clinicians who can deliver the chosen service.
 *
 * No service chosen means no constraint — nothing has been narrowed, so neither
 * does this.
 *
 * A clinician's `services` holds ids in several shapes (bare string,
 * `{serviceId}`, `{sid}`), and a plain `.includes` silently matches none of the
 * object ones.
 */
export function cliniciansForService(clinicians, serviceId) {
  const list = Array.isArray(clinicians) ? clinicians : [];
  const target = serviceId == null ? "" : String(serviceId).trim();
  if (!target) return list;
  return list.filter((clinician) => {
    const offered = Array.isArray(clinician?.services)
      ? clinician.services
      : [];
    return offered.some((reference) => {
      if (reference == null) return false;
      if (typeof reference === "object") {
        return [reference.serviceId, reference.sid, reference.id, reference._id]
          .filter((value) => value != null)
          .some((value) => String(value) === target);
      }
      return String(reference) === target;
    });
  });
}
